import React, { useEffect, useState } from "react";
import { Folder, Zap, Briefcase, Mail, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";

export default function AdminDashboard() {
    const [stats, setStats] = useState(null);
    const [latest, setLatest] = useState([]);

    useEffect(() => {
        api.get("/admin/stats").then((res) => setStats(res.data));
        api.get("/admin/submissions").then((res) => setLatest(res.data.slice(0, 5)));
    }, []);

    return (
        <div data-testid="admin-dashboard">
            <Header
                title="Dashboard"
                subtitle="Ringkasan konten portfolio dan pesan masuk dari client."
            />

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
                <StatCard label="Projects" value={stats?.projects ?? "—"} icon={Folder} to="/admin/projects" />
                <StatCard label="Skills" value={stats?.skills ?? "—"} icon={Zap} to="/admin/skills" />
                <StatCard label="Experiences" value={stats?.experiences ?? "—"} icon={Briefcase} to="/admin/experiences" />
                <StatCard
                    label="Inbox"
                    value={stats?.submissions ?? "—"}
                    badge={stats?.unread_submissions ? `${stats.unread_submissions} new` : null}
                    icon={Mail}
                    to="/admin/submissions"
                />
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 p-6 rounded-xl border border-white/5 bg-ink-800/40">
                    <div className="flex items-center justify-between mb-5">
                        <h3 className="font-display text-xl text-white">Recent inquiries</h3>
                        <Link to="/admin/submissions" className="text-sm text-brand hover:underline inline-flex items-center gap-1">
                            View all <ArrowRight size={14} />
                        </Link>
                    </div>
                    {latest.length === 0 ? (
                        <p className="text-ink-300 text-sm">Belum ada pesan masuk.</p>
                    ) : (
                        <div className="divide-y divide-white/5">
                            {latest.map((s) => (
                                <div key={s.id} className="py-4 flex items-start justify-between gap-4">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="text-white font-medium truncate">{s.name}</span>
                                            {!s.read && (
                                                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-brand text-white">NEW</span>
                                            )}
                                        </div>
                                        <div className="text-xs text-ink-300 font-mono">{s.email}</div>
                                        <div className="mt-1 text-sm text-ink-200 line-clamp-1">{s.subject || s.message}</div>
                                    </div>
                                    <div className="text-xs text-ink-400 font-mono whitespace-nowrap">
                                        {new Date(s.created_at).toLocaleDateString()}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="p-6 rounded-xl border border-white/5 bg-ink-800/40 space-y-4">
                    <h3 className="font-display text-xl text-white">Quick actions</h3>
                    <Link to="/admin/projects" className="block px-4 py-3 rounded-lg border border-white/5 bg-ink-900/60 hover:border-brand/40 transition-colors">
                        <div className="text-sm text-white">+ New project</div>
                        <div className="text-xs text-ink-300 font-mono">Add to your portfolio</div>
                    </Link>
                    <Link to="/admin/profile" className="block px-4 py-3 rounded-lg border border-white/5 bg-ink-900/60 hover:border-brand/40 transition-colors">
                        <div className="text-sm text-white">Edit hero video</div>
                        <div className="text-xs text-ink-300 font-mono">YouTube or uploaded URL</div>
                    </Link>
                    <Link to="/admin/skills" className="block px-4 py-3 rounded-lg border border-white/5 bg-ink-900/60 hover:border-brand/40 transition-colors">
                        <div className="text-sm text-white">+ Add a skill</div>
                        <div className="text-xs text-ink-300 font-mono">Show off your capabilities</div>
                    </Link>
                </div>
            </div>
        </div>
    );
}

function StatCard({ label, value, icon: Icon, to, badge }) {
    return (
        <Link
            to={to}
            data-testid={`stat-${label.toLowerCase()}`}
            className="group p-5 rounded-xl border border-white/5 bg-ink-800/40 hover:border-brand/40 transition-colors"
        >
            <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 rounded-md bg-ink-700/60 border border-white/5 flex items-center justify-center text-brand">
                    <Icon size={16} />
                </div>
                {badge && (
                    <span className="font-mono text-[10px] px-2 py-0.5 rounded bg-brand text-white">{badge}</span>
                )}
            </div>
            <div className="font-display text-4xl text-white tracking-tight">{value}</div>
            <div className="mono-label mt-1">{label}</div>
        </Link>
    );
}

export function Header({ title, subtitle, action }) {
    return (
        <header data-testid="admin-header" className="mb-10 flex flex-wrap items-end justify-between gap-4">
            <div>
                <h1 className="font-display text-4xl lg:text-5xl text-white tracking-tighter">{title}</h1>
                {subtitle && <p className="mt-2 text-ink-200">{subtitle}</p>}
            </div>
            {action}
        </header>
    );
}
